import { Content, ErrorPanel } from '@backstage/core-components';
import {
  WbBreadcrumb,
  WbBreadcrumbs,
  WbHeader,
} from '@agilelab/plugin-wb-platform';
import { useApi, useRouteRefParams } from '@backstage/core-plugin-api';
import { systemPrototypeDetailRouteRef } from '../../routes';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useQuery } from '@tanstack/react-query';
import { SystemPrototypeTemplateEntity } from '@agilelab/plugin-wb-builder-common';
import { TEMPLATE_FOR } from '@agilelab/plugin-wb-practice-shaper-common';
import { LinearProgress } from '@material-ui/core';
import { SystemPrototypeDetails } from '../SystemPrototypeDetails/SystemPrototypeDetails';
import { useSystemPrototype } from '../hooks/useSystemPrototype';
import { LayoutOptions } from '@agilelab/plugin-wb-scaffolder';
import { CustomViewSummaryMenu } from '@agilelab/plugin-wb-platform';
import { WbPage } from '@agilelab/plugin-wb-platform';

interface SystemPrototypeDetailsPageProps {
  layouts?: LayoutOptions[];
}

const normalizeEntityRef = (ref: string): string => {
  return ref.split('/').pop() || ref;
};

export const SystemPrototypeDetailsPage = ({
  layouts,
}: SystemPrototypeDetailsPageProps) => {
  const { id } = useRouteRefParams(systemPrototypeDetailRouteRef);
  const catalogApi = useApi(catalogApiRef);

  const {
    data: systemPrototype,
    isLoading: loadingPrototype,
    error,
  } = useSystemPrototype(Number(id));

  const { data: templates, isLoading: loadingTemplate } = useQuery({
    queryKey: ['systemPrototypeTemplates'],
    queryFn: async () => {
      const { items } = await catalogApi.getEntities({
        filter: { kind: 'SystemPrototypeTemplate' },
      });
      return items as SystemPrototypeTemplateEntity[];
    },
  });

  const matchingTemplate = templates?.find(template => {
    if (!systemPrototype) {
      return false;
    }

    const found = template.relations?.some(relation => {
      const matches =
        relation.type === TEMPLATE_FOR &&
        normalizeEntityRef(relation.targetRef) ===
          normalizeEntityRef(systemPrototype.systemPrototypeType);
      return matches;
    });

    return found;
  });

  if (loadingPrototype || loadingTemplate) return <LinearProgress />;

  if (error || !systemPrototype) {
    return (
      <ErrorPanel
        error={
          error instanceof Error ? error : new Error('Something went wrong')
        }
      />
    );
  }

  const getUrlById = (customViewId: string) =>
    `/platform-settings/custom-view/${customViewId}`;

  return (
    <WbPage themeId="home">
      <WbHeader
        titleStyle={{ display: 'flex', alignItems: 'center' }}
        pageTitleOverride={systemPrototype.name}
        title={
          <>
            {systemPrototype.name}{' '}
            <CustomViewSummaryMenu
              getUrlByid={getUrlById}
              customViewIds={['builder_system_prototype']}
              withMenu
            />
          </>
        }
        breadcrumbs={
          <WbBreadcrumbs aria-label="breadcrumb">
            <WbBreadcrumb>Builder</WbBreadcrumb>
            <WbBreadcrumb to="/prototypes">Backlog</WbBreadcrumb>
            <WbBreadcrumb>{systemPrototype.name}</WbBreadcrumb>
          </WbBreadcrumbs>
        }
      />
      <Content>
        <SystemPrototypeDetails
          prototype={systemPrototype}
          template={matchingTemplate}
          layouts={layouts}
        />
      </Content>
    </WbPage>
  );
};
